import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import FeatureAnnouncementService from "services/featureAnnouncement.service";
import HandleResponse from "../HandleResponse";
import HandleError from "../HandleError";
import {
  getMsUntilAnyEligible,
  markAnnouncementDismissed,
  parseAnnouncements,
  pickNextAnnouncement,
} from "../../pages/featureAnnouncement/featureAnnouncementStorage";

export const FeatureAnnouncementsActiveApi = createAsyncThunk(
  "featureAnnouncement/active",
  async (_, thunkAPI) => {
    try {
      const response = await FeatureAnnouncementService.fetchActive();
      return HandleResponse(thunkAPI, response, "active");
    } catch (error) {
      return HandleError(thunkAPI, error, "active");
    }
  }
);

const initialState = {
  announcements: [],
  currentAnnouncement: null,
  isOpen: false,
  isApiStatus: {
    FeatureAnnouncementsActiveApi: "",
  },
};

const featureAnnouncementSlice = createSlice({
  name: "featureAnnouncement",
  initialState,
  reducers: {
    ShowFeatureAnnouncement: (state, action) => {
      state.currentAnnouncement = action.payload;
      state.isOpen = !!action.payload;
    },
    CloseFeatureAnnouncement: (state) => {
      state.isOpen = false;
      state.currentAnnouncement = null;
    },
    RefreshCurrentFeatureAnnouncement: (state, action) => {
      const userId = action.payload?.userId;
      state.currentAnnouncement = pickNextAnnouncement(state.announcements, userId);
      state.isOpen = !!state.currentAnnouncement;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(FeatureAnnouncementsActiveApi.pending, (state) => {
        state.isApiStatus.FeatureAnnouncementsActiveApi = "loading";
      })
      .addCase(FeatureAnnouncementsActiveApi.fulfilled, (state, action) => {
        state.isApiStatus.FeatureAnnouncementsActiveApi = "succeeded";
        state.announcements = parseAnnouncements(action.payload);
      })
      .addCase(FeatureAnnouncementsActiveApi.rejected, (state) => {
        state.isApiStatus.FeatureAnnouncementsActiveApi = "failed";
        state.announcements = [];
      });
  },
});

export const {
  ShowFeatureAnnouncement,
  CloseFeatureAnnouncement,
  RefreshCurrentFeatureAnnouncement,
} = featureAnnouncementSlice.actions;

const scheduleAnnouncementReshow = (dispatch, getState, userId) => {
  const state = getState();
  let announcements = state.featureAnnouncement.announcements;

  if (!announcements?.length || !userId) {
    return;
  }

  const delayMs = getMsUntilAnyEligible(announcements, userId) ?? 60000;

  window.setTimeout(async () => {
    const latest = getState();
    if (latest.featureAnnouncement.isOpen) {
      return;
    }

    let list = latest.featureAnnouncement.announcements;
    if (!list?.length) {
      const action = await dispatch(FeatureAnnouncementsActiveApi());
      if (action.meta.requestStatus === "fulfilled") {
        list = parseAnnouncements(action.payload);
      }
    }

    const eligible = pickNextAnnouncement(list, userId);
    if (eligible?.id) {
      dispatch(ShowFeatureAnnouncement(eligible));
    }
  }, delayMs);
};

export const dismissFeatureAnnouncement =
  (announcement, userId, dismissedForever = false, showNextInQueue = true) =>
  async (dispatch, getState) => {
    const closedId = announcement?.id;

    if (announcement?.id) {
      markAnnouncementDismissed(announcement, userId, dismissedForever);
    }

    if (!showNextInQueue || dismissedForever) {
      dispatch(CloseFeatureAnnouncement());
      if (!dismissedForever) {
        scheduleAnnouncementReshow(dispatch, getState, userId);
      }
      return;
    }

    let announcements = getState().featureAnnouncement.announcements;

    if (!announcements?.length) {
      const action = await dispatch(FeatureAnnouncementsActiveApi());
      if (action.meta.requestStatus === "fulfilled") {
        announcements = parseAnnouncements(action.payload);
      }
    }

    const next = pickNextAnnouncement(announcements, userId, {
      afterDismissId: closedId,
    });

    if (next?.id) {
      dispatch(ShowFeatureAnnouncement(next));
      return;
    }

    dispatch(CloseFeatureAnnouncement());
    scheduleAnnouncementReshow(dispatch, getState, userId);
  };

export default featureAnnouncementSlice.reducer;
